// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
  Divider,
  // ListItem,
  // ListItemText,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import DataTable from "theme/Tables/DataTable";
// import MDBadge from "theme/components/MDBadge";
import MDButton from "theme/components/MDButton";
import usePageTitle from "app/pageTitle";

function AgentLicensesPage() {
  const pageTitle = "Agent Licenses";
  usePageTitle(pageTitle);
  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };
  const [pageLoaded, setPageLoaded] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);

  const [isPlanEnabled, setIsPlanEnabled] = useState(false);
  const [currency, setCurrency] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [availableLicenses, setAvailableLicenses] = useState(0);
  const [licensePrice, setLicensePrice] = useState(null);
  const [minimumQty, setMinimumQty] = useState(null);
  const [vat, setVat] = useState(0.075);
  const [subscriptionPaymentDuration, setSubscriptionPaymentDuration] = useState(null);
  const [purchaseName, setPurchaseName] = useState(null);
  const [licenseQty, setLicenseQty] = useState(null);
  const [openPAGBundleDialog, setOpenPAGBundleDialog] = useState(false);

  const tablePurchasesColumns = [
    { Header: "Name", accessor: "name", align: "left" },
    { Header: "Purchased On", accessor: "created", align: "left" },
    { Header: "Expires On", accessor: "expires_on", align: "left" },
    { Header: "Licenses", accessor: "quantity", align: "center" },
    { Header: "Available", accessor: "free", align: "center" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildPurchasesRows = (data) => {
    const rows = [];
    data.map((purchase) => {
      rows.push({
        name: purchase.title,
        created: purchase.created,
        expires_on: purchase.expires_on,
        quantity: purchase.quantity,
        free: purchase.status ? purchase.quantity - purchase.used : 0,
        status: (
          <MDBox>
            <Icon color={purchase.status ? "success" : "error"}>
              {purchase.status ? "check_circle" : "cancel"}
            </Icon>
          </MDBox>
        ),
        actions: (
          <MDBox mt={0} variant="button">
            <MDButton
              size="small"
              variant="outlined"
              color="dark"
              iconOnly
              onClick={() => {
                navigate(`/licenses/${purchase.reference}`);
              }}
            >
              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                east
              </Icon>
            </MDButton>
          </MDBox>
        ),
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("portal/account/licenses.json")
        .then((result) => {
          // console.log(result);
          setPageLoaded(true);
          setShowLoading(false);
          if (result.data.status === "success") {
            setIsPlanEnabled(result.data.data.isPlanEnabled);
            if (result.data.data.isPlanEnabled) {
              setAvailableLicenses(result.data.data.available);
              setCurrency(result.data.data.currency);
              setVat(result.data.data.vat);
              setLicensePrice(result.data.data.price);
              setMinimumQty(result.data.data.minimum_quantity);
              const rows = buildPurchasesRows(result.data.data.purchases);
              setPurchases(rows);
            }
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  function getLicenseFee() {
    if (licenseQty === null || licenseQty <= 0) {
      return 0;
    }

    if (subscriptionPaymentDuration === null || subscriptionPaymentDuration <= 0) {
      return 0;
    }

    return (licensePrice * subscriptionPaymentDuration * licenseQty) / 100;
  }

  function getTotalLicenseFee() {
    const c = getLicenseFee();
    const v = parseFloat(vat) * c;
    return c + v;
  }

  async function buyLicense() {
    if (purchaseName == null || purchaseName.length < 3) {
      setSeverity("error");
      setErrMsg("Please enter a purchase name/description.");
      setOpen(true);
      return;
    }

    if (licenseQty === null) {
      setSeverity("error");
      setErrMsg("License quantity is required.");
      setOpen(true);
      return;
    }

    const qty = parseInt(licenseQty, 10);
    if (qty <= 0) {
      setSeverity("error");
      setErrMsg("Please enter a valid quantity.");
      setOpen(true);
      return;
    }

    if (qty < minimumQty) {
      setSeverity("error");
      setErrMsg(`A minimum of ${minimumQty} licenses per purchase is required.`);
      setOpen(true);
      return;
    }

    if (subscriptionPaymentDuration == null || subscriptionPaymentDuration <= 0) {
      setSeverity("error");
      setErrMsg("Please select a duration");
      setOpen(true);
      return;
    }

    if (window.confirm(`Proceed with payment?`) === false) {
      return;
    }

    setOpenPAGBundleDialog(false);
    setShowLoading(true);
    const json = {
      name: purchaseName,
      quantity: qty,
      duration: subscriptionPaymentDuration,
    };

    await axios
      .post("portal/account/licenses/buy.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openPAGBundleDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox mx={1}>Purchase License</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox mt={0} mb={3}>
            <MDBox p={1}>
              <MDBox mt={0}>
                <MDInput
                  placeholder="Ex. Nestle Campaign"
                  name="name"
                  margin="dense"
                  label="Purchase Name"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={purchaseName}
                  onChange={(e) => setPurchaseName(e.target.value)}
                />
              </MDBox>
              <MDBox mt={3} mb={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6} lg={6}>
                    <MDBox mt={-1}>
                      <MDInput
                        placeholder="1"
                        name="qty"
                        margin="dense"
                        label="Quantity"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={licenseQty}
                        onChange={(e) => setLicenseQty(e.target.value)}
                      />
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={6} lg={6}>
                    <MDBox mt={0}>
                      <FormControl sx={{ minWidth: 120, width: "100%" }}>
                        <InputLabel>Duration</InputLabel>
                        <Select
                          label="Duration"
                          onChange={(e) => {
                            setSubscriptionPaymentDuration(e.target.value);
                          }}
                          sx={{ padding: "12px", paddingLeft: "3px" }}
                        >
                          <MenuItem value="1">1 Month</MenuItem>
                          <MenuItem value="2">2 Months</MenuItem>
                          <MenuItem value="3">3 Months</MenuItem>
                          <MenuItem value="6">6 Months</MenuItem>
                          <MenuItem value="12">12 Months</MenuItem>
                        </Select>
                      </FormControl>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>
              <MDBox mt={3} px={3}>
                <Divider />
              </MDBox>
              <MDBox mt={3} px={0}>
                <MDBox display="flex" justifyContent="space-between" mt={1}>
                  <MDBox>
                    <MDTypography variant="subtitle2" fontSize={14}>
                      License Amount:
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="subtitle2" fontWeight="regular" fontSize={14}>
                      {currency}
                      {getLicenseFee().toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" mt={1}>
                  <MDBox>
                    <MDTypography variant="subtitle2" fontSize={14}>
                      VAT Fee:
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="subtitle2" fontWeight="regular" fontSize={14}>
                      {currency}
                      {(parseFloat(vat) * getLicenseFee()).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </MDTypography>
                  </MDBox>
                </MDBox>
                <MDBox>
                  <Divider />
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" mt={1}>
                  <MDBox>
                    <MDTypography variant="subtitle2" fontSize={14}>
                      Total Amount:
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="subtitle2" fontWeight="regular" fontSize={14}>
                      {currency}
                      {getTotalLicenseFee().toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
              <MDBox mt={3} px={0} align="center">
                <MDTypography variant="subtitle2" fontWeight="light" fontSize={12}>
                  {currency}
                  {(licensePrice / 100).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}{" "}
                  / License
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenPAGBundleDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="success"
            variant="contained"
            onClick={() => {
              buyLicense();
            }}
          >
            Purchase
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox pt={3} pb={3}>
        {pageLoaded ? (
          <MDBox>
            <MDBox>
              {isPlanEnabled ? (
                <MDBox>
                  <MDBox>
                    <Grid container spacing={3}>
                      <Grid item xs={8} md={8} lg={8}>
                        <MDBox sx={{ height: "100%" }}>
                          <Card sx={{ height: "100%" }}>
                            <MDBox p={3}>
                              <MDTypography variant="h6">Licenses</MDTypography>
                              <MDTypography variant="body2">
                                Purchase and manage licenses on demand for your agents and agent
                                groups.
                              </MDTypography>
                              <br />
                              <MDTypography variant="body2">
                                Licenses have a default 30-day expiration date.
                              </MDTypography>
                            </MDBox>
                          </Card>
                        </MDBox>
                      </Grid>
                      <Grid item xs={4} md={4} lg={4}>
                        <MDBox sx={{ height: "100%" }}>
                          <Card sx={{ height: "100%" }}>
                            <MDBox p={3} align="center">
                              <MDBox>
                                <MDBox sx={{ position: "relative", display: "inline-flex" }}>
                                  <CircularProgress
                                    variant="determinate"
                                    size={100}
                                    thickness={3}
                                    value={100}
                                    color={availableLicenses <= 0 ? "error" : "success"}
                                  />
                                  <MDBox
                                    sx={{
                                      top: 0,
                                      left: 0,
                                      bottom: 0,
                                      right: 0,
                                      position: "absolute",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <MDTypography
                                      variant="caption"
                                      component="div"
                                      color={availableLicenses <= 0 ? "error" : "success"}
                                      fontSize={20}
                                    >
                                      <b>{availableLicenses}</b>
                                    </MDTypography>
                                  </MDBox>
                                </MDBox>
                              </MDBox>
                              <MDTypography variant="h6" fontWeight="regular">
                                Available Licenses
                              </MDTypography>
                            </MDBox>
                          </Card>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </MDBox>
                  <MDBox mt={3}>
                    <Card sx={{ height: "100%", marginTop: "0px" }}>
                      <MDBox>
                        <MDBox
                          p={2}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <MDTypography variant="h6" fontWeight="medium">
                            Purchases
                          </MDTypography>
                          <MDBox>
                            <MDButton
                              variant="contained"
                              color="success"
                              size="small"
                              onClick={() => {
                                setLicenseQty(minimumQty);
                                setSubscriptionPaymentDuration(null);
                                setOpenPAGBundleDialog(true);
                              }}
                              startIcon={<Icon sx={{ fontWeight: "bold" }}>add</Icon>}
                            >
                              Purchase
                            </MDButton>
                          </MDBox>
                        </MDBox>
                        <MDBox mt={5}>
                          <DataTable
                            table={{ columns: tablePurchasesColumns, rows: purchases }}
                            isSorted
                            entriesPerPage={false}
                            showTotalEntries
                            noEndBorder
                          />
                        </MDBox>
                      </MDBox>
                    </Card>
                  </MDBox>
                </MDBox>
              ) : (
                <MDBox>
                  <Card>
                    <MDBox p={3} align="center">
                      <MDBox>
                        <Icon color="dark">cancel</Icon>
                      </MDBox>
                      <MDBox mt={2}>
                        <MDTypography variant="body2">
                          Your account plan is not enabled for PAY AS YOU GO
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </Card>
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        ) : (
          <> </>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AgentLicensesPage;
